'use client';
import React, { useState, useRef, useEffect } from 'react';

interface Category {
  id: string;
  label: string;
}

const categories: Category[] = [
  { id: 'all', label: 'Most Popular' },
  { id: 'music', label: 'Music & Concerts' },
  { id: 'tech', label: 'Tech & Innovation' },
  { id: 'food', label: 'Food & Drink' },
  { id: 'sports', label: 'Sports & Fitness' },
  { id: 'arts', label: 'Arts & Culture' },
  { id: 'business', label: 'Business' },
  { id: 'wellness', label: 'Health & Wellness' },
  { id: 'comedy', label: 'Comedy' },
  { id: 'family', label: 'Family' },
  { id: 'outdoor', label: 'Outdoor' },
  { id: 'education', label: 'Education' },
];

interface CategoryBarProps {
  onCategoryChange?: (id: string) => void;
}

export default function CategoryBar({ onCategoryChange }: CategoryBarProps) {
  const [active, setActive] = useState('all');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleSelect = (id: string) => {
    setActive(id);
    onCategoryChange?.(id);
  };

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  return (
    <div
      className="sticky z-40"
      style={{ top: '52px', background: '#1A0A00', borderBottom: '1px solid rgba(201,168,76,0.25)' }}
    >
      <div className="flex items-center" style={{ padding: '0 48px', height: '64px' }}>
        {/* Left Arrow */}
        <button
          onClick={() => scroll('left')}
          aria-label="Scroll left"
          className="flex-shrink-0 flex items-center justify-center transition-all duration-200 mr-2"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: `1px solid ${canScrollLeft ? 'rgba(201,168,76,0.6)' : 'rgba(201,168,76,0.2)'}`,
            background: canScrollLeft ? 'rgba(201,168,76,0.15)' : 'transparent',
            color: canScrollLeft ? '#C9A84C' : 'rgba(201,168,76,0.3)',
            cursor: canScrollLeft ? 'pointer' : 'default',
            flexShrink: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Scrollable Category List */}
        <div
          ref={scrollRef}
          className="flex items-center gap-2 overflow-x-auto flex-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleSelect(cat.id)}
              className="flex-shrink-0 transition-all duration-200"
              style={{
                height: '34px',
                padding: '0 18px',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: active === cat.id ? 500 : 400,
                whiteSpace: 'nowrap',
                background: active === cat.id ? 'linear-gradient(135deg, #C9A84C, #8B6914)' : 'rgba(201,168,76,0.08)',
                color: active === cat.id ? '#FAF7F2' : 'rgba(201,168,76,0.7)',
                border: active === cat.id ? 'none' : '1px solid rgba(201,168,76,0.2)',
                cursor: 'pointer',
                fontFamily: 'Roboto, sans-serif',
                letterSpacing: '0.3px',
                boxShadow: active === cat.id ? '0 2px 8px rgba(201,168,76,0.3)' : 'none',
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scroll('right')}
          aria-label="Scroll right"
          className="flex-shrink-0 flex items-center justify-center transition-all duration-200 ml-2"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: `1px solid ${canScrollRight ? 'rgba(201,168,76,0.6)' : 'rgba(201,168,76,0.2)'}`,
            background: canScrollRight ? 'rgba(201,168,76,0.15)' : 'transparent',
            color: canScrollRight ? '#C9A84C' : 'rgba(201,168,76,0.3)',
            cursor: canScrollRight ? 'pointer' : 'default',
            flexShrink: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
